import {TFunction} from 'i18next';
import {assign, isNumber} from 'lodash';
import {Fragment, ReactText, useCallback, useEffect, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {toast, ToastOptions} from 'react-toastify';

import {Loader} from 'components/Loader';
import {Icon, IconColor, IconName} from 'components/UIKit/Icon';
import {TRANSLATION_KEYS} from 'locale';
import {ToastProps} from 'types';

import {LinearProgress} from '../LinearProgress';

import {AUTO_CLOSE_DEFAULT} from './constants';

const T_KEYS = TRANSLATION_KEYS.UI_KIT.NOTIFIER;

import './styles.scss';

const getIconComponent = (props: ToastProps, IconComponent: React.ReactElement) =>
    props.IconComponent || (props.icon ? undefined : IconComponent);

type NotifierIconProps = {
    name: IconName;
    color: IconColor;
}
const NotifierIcon: React.FC<NotifierIconProps> = ({name, color}: NotifierIconProps) => (
    <div className='tw-w-8 tw-h-8 tw-bg-light tw-flex-center tw-rounded-full'><Icon name={name} color={color} size='lg' /></div>
);

const Notifier: React.FC<ToastProps> = ({title, progress, details, icon, IconComponent}: ToastProps) => (
    <Fragment>
        {(icon || IconComponent) &&
            <div className='vs-icon tw-mr-6 tw-w-8 tw-relative tw-flex tw-justify-center tw-items-center'>
                {IconComponent || <Icon name={icon} className='tw-h-8 tw-w-8' />}
            </div>}
        <div className='tw-flex tw-flex-col tw-justify-center tw-flex-grow'>
            <div className='vs-title tw-text-lg tw-text-break-word tw-flex tw-justify-between tw-items-center'>
                {title}
                {progress && <div className='tw-ml-2 tw-w-12 tw-text-right tw-text-default'>{`${progress}%`}</div>}
            </div>
            {details && <div className='vs-details'>{details}</div>}
            {isNumber(progress) &&
                <div className='tw-mt-2'><LinearProgress color='primary' value={progress} /></div>}
        </div>

    </Fragment>
);

const openInfo = (props: ToastProps, toastProps?: ToastOptions) => toast.info(<Notifier {...props} />, toastProps);

const renderError = (props: ToastProps) =>
    <Notifier {...props} IconComponent={getIconComponent(props, <NotifierIcon name='close' color='alert' />)} />;

const openError = (props: ToastProps, toastProps?: ToastOptions) =>
    toast.error(renderError(props), toastProps);

const onUpdateError = (id: ReactText, props: ToastProps, toastProps?: ToastOptions) => {
    toast.update(id, {
        render: <Notifier {...props} IconComponent={getIconComponent(props, <NotifierIcon name='close' color='alert' />)} />,
        type: toast.TYPE.ERROR,
        ...toastProps
    });
};

const renderWarning = (props: ToastProps) =>
    <Notifier {...props} IconComponent={getIconComponent(props, <NotifierIcon name='warning' color='warning' />)} />;

const openWarning = (props: ToastProps, toastProps?: ToastOptions) =>
    toast.warning(renderWarning(props), toastProps);

const onUpdateWarning = (id: ReactText, props: ToastProps, toastProps?: ToastOptions) => {
    toast.update(id, {
        render: <Notifier {...props} IconComponent={getIconComponent(props, <NotifierIcon name='warning' color='warning' />)} />,
        type: toast.TYPE.WARNING,
        ...toastProps
    });
};

const renderSuccess = (props: ToastProps) => (
    <Notifier
        {...props}
        IconComponent={getIconComponent(props, <NotifierIcon name='check' color='success' />)}
    />
);
const openSuccess = (props: ToastProps, toastProps?: ToastOptions) =>
    toast.success(renderSuccess(props), toastProps);

const onUpdateSuccess = (id: ReactText, props: ToastProps, toastProps?: ToastOptions) => {
    toast.update(id, {
        render: renderSuccess(props),
        type: toast.TYPE.SUCCESS,
        ...toastProps
    });
};

const renderLoading = (props: ToastProps) => (
    <Notifier
        {...props}
        IconComponent={getIconComponent(props, <Loader colorClassName='tw-text-alternative' strokeWidth={4} />)}
    />
);

const openLoading = (props: ToastProps, toastProps?: ToastOptions) => toast.dark(
    renderLoading(props),
    {...toastProps, autoClose: false}
);

const onUpdateLoading = (id: ReactText, props: ToastProps, toastProps?: ToastOptions) => {
    toast.update(id, {
        render: renderLoading(props),
        type: toast.TYPE.DARK,
        ...toastProps
    });
};

const getOpenedWrapper = (id: React.ReactText) => ({
    id,
    onLoading: (props: ToastProps, options?: ToastOptions) => onUpdateLoading(id, props, {autoClose: AUTO_CLOSE_DEFAULT, ...options}),
    onSuccess: (props: ToastProps, options?: ToastOptions) => onUpdateSuccess(id, props, {autoClose: AUTO_CLOSE_DEFAULT, ...options}),
    onError: (props: ToastProps, options?: ToastOptions) => onUpdateError(id, props, {autoClose: AUTO_CLOSE_DEFAULT, ...options}),
    onWarning: (props: ToastProps, options?: ToastOptions) => onUpdateWarning(id, props, {autoClose: AUTO_CLOSE_DEFAULT, ...options})
});

const openWrappedLoading = (props: ToastProps, toastProps?: ToastOptions) => getOpenedWrapper(openLoading(props, toastProps));

export enum PredefinedNotifierType {
    FetchError = 'fetch-error',
    Save = 'save',
    SaveError = 'save-error',
    SendError = 'send-error',
    Send = 'send',
    Delete = 'delete',
}

const fetchError = (t: TFunction) => ({
    onSuccess: () => null,
    onError: (errorProps?: ToastProps, errorToastProps?: ToastOptions) =>
        openError(assign({title: t(T_KEYS.PREDEFINED.FETCH.ERROR)}, errorProps), errorToastProps),
    onWarning: () => null
});

const saveLoading = (t: TFunction, props?: ToastProps, toastProps?: ToastOptions) => {
    const loadingNotifier = openWrappedLoading(assign({title: t(T_KEYS.PREDEFINED.SAVE.LOADING)}, props), toastProps);
    return {
        ...loadingNotifier,
        onSuccess: (successProps?: ToastProps, successToastProps?: ToastOptions) =>
            loadingNotifier.onSuccess(assign({title: t(T_KEYS.PREDEFINED.SAVE.SUCCESS)}, successProps), successToastProps),
        onError: (errorProps?: ToastProps, errorToastProps?: ToastOptions) =>
            loadingNotifier.onError(assign({title: t(T_KEYS.PREDEFINED.SAVE.ERROR)}, errorProps), errorToastProps),
        onWarning: (warningProps?: ToastProps, warningToastProps?: ToastOptions) =>
            loadingNotifier.onWarning(assign({title: t(T_KEYS.PREDEFINED.SAVE.WARNING)}, warningProps), warningToastProps)
    };
};

const saveError = (t: TFunction) => ({
    onSuccess: () => null,
    onError: (errorProps?: ToastProps, errorToastProps?: ToastOptions) =>
        openError(assign({title: t(T_KEYS.PREDEFINED.SAVE.ERROR)}, errorProps), errorToastProps),
    onWarning: () => null
});

const sendLoading = (t: TFunction, props?: ToastProps, toastProps?: ToastOptions) => {
    const loadingNotifier = openWrappedLoading(assign({title: t(T_KEYS.PREDEFINED.SEND.LOADING)}, props), toastProps);
    return {
        ...loadingNotifier,
        onSuccess: (successProps?: ToastProps, successToastProps?: ToastOptions) =>
            loadingNotifier.onSuccess(assign({title: t(T_KEYS.PREDEFINED.SEND.SUCCESS)}, successProps), successToastProps),
        onError: (errorProps?: ToastProps, errorToastProps?: ToastOptions) =>
            loadingNotifier.onError(assign({title: t(T_KEYS.PREDEFINED.SEND.ERROR)}, errorProps), errorToastProps)
    };
};

const sendError = (t: TFunction) => ({
    onSuccess: () => null,
    onError: (errorProps?: ToastProps, errorToastProps?: ToastOptions) =>
        openError(assign({title: t(T_KEYS.PREDEFINED.SEND.ERROR)}, errorProps), errorToastProps),
    onWarning: () => null
});

const deleteLoading = (t: TFunction, props?: ToastProps, toastProps?: ToastOptions) => {
    const loadingNotifier = openWrappedLoading(assign({title: t(T_KEYS.PREDEFINED.DELETE.LOADING)}, props), toastProps);
    return {
        ...loadingNotifier,
        onSuccess: (successProps?: ToastProps, successToastProps?: ToastOptions) =>
            loadingNotifier.onSuccess(assign({title: t(T_KEYS.PREDEFINED.DELETE.SUCCESS)}, successProps), successToastProps),
        onError: (errorProps?: ToastProps, errorToastProps?: ToastOptions) =>
            loadingNotifier.onError(assign({title: t(T_KEYS.PREDEFINED.DELETE.ERROR)}, errorProps), errorToastProps),
    };
};

export const predefinedNotifier = {
    [PredefinedNotifierType.FetchError]: fetchError,
    [PredefinedNotifierType.Save]: saveLoading,
    [PredefinedNotifierType.SaveError]: saveError,
    [PredefinedNotifierType.Send]: sendLoading,
    [PredefinedNotifierType.SendError]: sendError,
    [PredefinedNotifierType.Delete]: deleteLoading,
};

export const notifier = {
    info: openInfo,
    error: openError,
    warning: openWarning,
    success: openSuccess,
    loading: openWrappedLoading,
    wrap: getOpenedWrapper,
    isActive: toast.isActive
};

type Props = {
    labels?: {pending?: string, success?: string, error?: string},
    options?: ToastOptions
}

export const useProgressToast = ({labels, options}: Props) => {
    const {t} = useTranslation();
    const toastId = useRef(null);
    const actualOptions = useRef(null);

    const defaultLabels = useMemo(() => ({
        pending: t(T_KEYS.PREDEFINED.PROGRESS.LOADING),
        success: t(T_KEYS.PREDEFINED.PROGRESS.SUCCESS),
        error: t(T_KEYS.PREDEFINED.PROGRESS.ERROR)
    }), [t]);

    const {pending, success, error} = useMemo(() => ({...defaultLabels, ...labels}), [labels, defaultLabels]);

    const notifyProgressStart = useCallback(() => {
        toastId.current = toast.info(<Notifier title={pending} />, {...actualOptions.current, autoClose: false});
    }, [pending]);

    const updateProgress = useCallback((progress: number) => toastId.current ?
        toast.update(toastId.current, {
            render: <Notifier title={pending} progress={progress} />,
        }) : null, [pending]);

    const notifySuccess = useCallback(() => toastId.current ?
        toast.update(toastId.current, {
            render: <Notifier title={success} />,
            type: toast.TYPE.SUCCESS,
            autoClose: 3000
        }) : null, [success]);

    const notifyError = useCallback(() => toastId.current ?
        toast.update(toastId.current, {
            render: <Notifier title={error} />,
            type: toast.TYPE.ERROR,
            autoClose: 3000
        }) : null, [error]);

    useEffect(() => {
        actualOptions.current = options;
    }, [options]);

    return {
        notifyProgressStart,
        updateProgress,
        notifySuccess,
        notifyError
    };
};